import type {
  ControlWeather,
  ScheduleOfficialForecast,
  ScheduleWeather,
  TrackWeek,
  UserPreferences,
} from "../types";

type Units = UserPreferences["units"];

export function formatScheduleTempRange(
  forecast: ScheduleOfficialForecast | null | undefined,
  units: Units,
): string | null {
  if (!forecast) return null;
  if (units === "metric") {
    return formatRange(forecast.tempLowC, forecast.tempHighC, " C");
  }
  return formatRange(forecast.tempLowF, forecast.tempHighF, " F");
}

export function formatProjectedTemp(
  control: ControlWeather | null | undefined,
  units: Units,
): string | null {
  if (!control) return null;
  const value = units === "metric" ? control.tempC : control.tempF;
  if (value == null) return null;
  return `${Math.round(value)} ${units === "metric" ? "C" : "F"}`;
}

export type WindDirection8 = "N" | "NE" | "E" | "SE" | "S" | "SW" | "W" | "NW";

// Categorical offsets applied per 8-way wind direction. Seeded at zero until
// calibration observations from `.ibt` telemetry populate real values per the
// model brief (transfer/claude-track-weather-model-update.md). The slot stays
// here so the shape is stable when calibration data arrives.
export const DIRECTION_OFFSET_F: Record<WindDirection8, number> = {
  N: 0,
  NE: 0,
  E: 0,
  SE: 0,
  S: 0,
  SW: 0,
  W: 0,
  NW: 0,
};

export interface ProjectedTrackTempEstimate {
  valueF: number;
  valueC: number;
  lowF: number;
  highF: number;
  lowC: number;
  highC: number;
  confidence: "high" | "medium" | "low";
  factors: string[];
  windDirection: WindDirection8 | null;
  directionOffsetF: number;
}

export function estimateProjectedTrackTemp(
  control: ControlWeather | null | undefined,
  sessionTimeLabel?: string | null,
  dateHint?: string | null,
): ProjectedTrackTempEstimate | null {
  if (!control) return null;
  const airF = control.tempF ?? (control.tempC != null ? cToF(control.tempC) : null);
  if (airF == null) return null;

  const cloudPct = resolveCloudCoverPct(control);
  const humidityPct = clamp(control.humidityPct ?? 55, 0, 100);
  const windMph = control.windMph ?? (control.windKph != null ? control.windKph * 0.621371 : 6);
  const hour = parseSessionHour(sessionTimeLabel);
  const solar = estimateSolarFactor(hour, dateHint);
  const cloudFraction = cloudPct != null ? clamp(cloudPct / 100, 0, 1) : 0.45;

  // Clear sky lets more shortwave radiation reach asphalt; overcast still allows diffuse heating.
  const cloudTransmission = clamp(1 - 0.72 * Math.pow(cloudFraction, 1.35), 0.22, 1);
  const windMs = Math.max(0, windMph) * 0.44704;
  const hConv = 5.7 + 3.8 * windMs;
  const hRadiation = 5.5;
  const hGroundStorage = 18;
  const hEffective = hConv + hRadiation + hGroundStorage;

  const asphaltAbsorptivity = 0.92;
  const peakShortwaveWm2 = 850;
  const solarDeltaC = (asphaltAbsorptivity * peakShortwaveWm2 * solar.factor * cloudTransmission) / hEffective;
  const solarDeltaF = cToFDelta(solarDeltaC);

  const middayDeltaF = cToFDelta(
    (asphaltAbsorptivity * peakShortwaveWm2 * cloudTransmission) / hEffective,
  );
  const storedHeatF = estimateStoredHeatF(hour, solar, middayDeltaF);
  const clearNightCoolingF = estimateNightCoolingF(solar.factor, cloudFraction, humidityPct, windMph);
  const humidBufferF = solar.factor < 0.15 ? ((humidityPct - 50) / 50) * 1.2 : 0;

  const windDirection = parseWindDirection8(control.windDirectionCardinal);
  const directionOffsetF = windDirection ? DIRECTION_OFFSET_F[windDirection] : 0;

  const valueF = clamp(
    airF + solarDeltaF + storedHeatF + humidBufferF - clearNightCoolingF + directionOffsetF,
    airF - 8,
    airF + 52,
  );
  const uncertaintyF =
    (hour == null ? 3 : 0) +
    (cloudPct == null ? 2 : 0) +
    ((control.windMph == null && control.windKph == null) ? 1.5 : 0) +
    (windDirection == null ? 1.5 : 0) +
    4;
  const rounded = Math.round(valueF);
  const lowF = Math.round(valueF - uncertaintyF);
  const highF = Math.round(valueF + uncertaintyF);
  const confidence = uncertaintyF <= 4.5 ? "high" : uncertaintyF <= 7 ? "medium" : "low";

  const factors = [
    solar.factor > 0.65 ? "high sun load" : solar.factor > 0.2 ? "partial sun load" : "low sun load",
    cloudPct != null ? `${Math.round(cloudPct)}% cloud cover` : "cloud cover estimated",
    windMph > 12 ? "wind cooling active" : windMph < 4 ? "light wind cooling" : "moderate wind cooling",
    hour == null ? "session time unknown" : "thermal lag included",
    windDirection ? `wind from ${windDirection}` : "wind direction unknown",
  ];

  return {
    valueF: rounded,
    valueC: Math.round(fToC(valueF)),
    lowF,
    highF,
    lowC: Math.round(fToC(lowF)),
    highC: Math.round(fToC(highF)),
    confidence,
    factors,
    windDirection,
    directionOffsetF,
  };
}

const WIND_DIRECTION_8: readonly WindDirection8[] = [
  "N", "NE", "E", "SE", "S", "SW", "W", "NW",
];

export function parseWindDirection8(raw: string | null | undefined): WindDirection8 | null {
  if (!raw) return null;
  const normalized = raw.trim().toUpperCase().replace(/[^A-Z]/g, "");
  if (!normalized) return null;
  if ((WIND_DIRECTION_8 as readonly string[]).includes(normalized)) {
    return normalized as WindDirection8;
  }
  // Tolerate spelled-out forms ("NORTH", "NORTHEAST", "SOUTH-SOUTHWEST" → first match).
  const aliasMap: Record<string, WindDirection8> = {
    NORTH: "N",
    NORTHEAST: "NE",
    EAST: "E",
    SOUTHEAST: "SE",
    SOUTH: "S",
    SOUTHWEST: "SW",
    WEST: "W",
    NORTHWEST: "NW",
  };
  if (aliasMap[normalized]) return aliasMap[normalized];
  // 16-way safety net (iRacing only exposes 8, but the string field is loose):
  // snap to whichever single letter appears twice. NNE→N, ENE→E, ESE→E, SSE→S, etc.
  if (normalized.length === 3) {
    const counts: Record<string, number> = {};
    for (const ch of normalized) counts[ch] = (counts[ch] || 0) + 1;
    const doubled = Object.keys(counts).find((k) => counts[k] === 2);
    if (doubled && (WIND_DIRECTION_8 as readonly string[]).includes(doubled)) {
      return doubled as WindDirection8;
    }
  }
  return null;
}

export function formatProjectedTrackTemp(
  estimate: ProjectedTrackTempEstimate | null,
  units: Units,
): string | null {
  if (!estimate) return null;
  if (units === "metric") {
    return `${estimate.valueC} C est. (${estimate.lowC}-${estimate.highC} C)`;
  }
  return `${estimate.valueF} F est. (${estimate.lowF}-${estimate.highF} F)`;
}

export function formatProjectedWind(
  control: ControlWeather | null | undefined,
  units: Units,
): string | null {
  if (!control) return null;
  const value = units === "metric" ? control.windKph : control.windMph;
  if (value == null) return control.windDirectionCardinal || null;
  const unitLabel = units === "metric" ? "kph" : "mph";
  const base = `${Math.round(value)} ${unitLabel}`;
  return control.windDirectionCardinal ? `${base} ${control.windDirectionCardinal}` : base;
}

export function formatProjectedCloud(
  control: ControlWeather | null | undefined,
  forecast?: ScheduleOfficialForecast | null,
): string | null {
  if (!control && !forecast) return null;
  if (control?.cloudLabel) return control.cloudLabel;

  const fallbackLabel = forecast?.skyLowLabel ?? forecast?.skyHighLabel ?? forecast?.skyLabel;
  if (fallbackLabel) return fallbackLabel;

  const fallbackPct = control?.cloudCoverPct ?? forecast?.cloudLowPct ?? forecast?.cloudHighPct;
  return fallbackPct != null ? `${Math.round(fallbackPct)}%` : null;
}

export function formatProjectedHumidity(
  control: ControlWeather | null | undefined,
  forecast?: ScheduleOfficialForecast | null,
): string | null {
  if (control?.humidityPct != null) {
    return `${Math.round(control.humidityPct)}%`;
  }
  if (forecast?.humidityLowPct != null) {
    return `${Math.round(forecast.humidityLowPct)}%`;
  }
  if (forecast?.humidityHighPct != null) {
    return `${Math.round(forecast.humidityHighPct)}%`;
  }
  return null;
}

export function formatOfficialHumidityRange(
  forecast: ScheduleOfficialForecast | null | undefined,
): string | null {
  if (!forecast) return null;
  return formatRange(forecast.humidityLowPct, forecast.humidityHighPct, "%");
}

export function formatOfficialCloudRange(
  forecast: ScheduleOfficialForecast | null | undefined,
): string | null {
  if (!forecast) return null;
  return formatRange(forecast.cloudLowPct, forecast.cloudHighPct, "%");
}

export function formatOfficialWindRange(
  forecast: ScheduleOfficialForecast | null | undefined,
  units: Units,
): string | null {
  if (!forecast) return null;
  if (units === "metric") {
    return formatRange(forecast.windLowKph, forecast.windHighKph, " kph");
  }
  return formatRange(forecast.windLowMph, forecast.windHighMph, " mph");
}

export function formatPrecipChance(
  forecast: ScheduleOfficialForecast | null | undefined,
): string | null {
  if (!forecast || forecast.precipChancePct == null) return null;
  if (forecast.precipChancePct <= 0.4) return "None";
  return `${Math.round(forecast.precipChancePct)}%`;
}

export function buildWeatherShareText(
  week: Pick<
    TrackWeek,
    "trackName" | "seriesName" | "displayWeekNumber" | "weekNumber" | "startsAt" | "endsAt"
  >,
  weather: ScheduleWeather,
  units: Units,
): string {
  const weekLabel = week.displayWeekNumber ?? week.weekNumber;
  const trackEstimate = estimateProjectedTrackTemp(
    weather.controlWeather,
    weather.sessionTimeLabel,
    week.startsAt,
  );
  const trackTemp = formatProjectedTrackTemp(trackEstimate, units);
  const lines = [
    `${week.trackName} - Projected Weather`,
    [week.seriesName, weekLabel != null ? `Week ${weekLabel}` : "", formatDateRange(week.startsAt, week.endsAt)]
      .filter(Boolean)
      .join(" | "),
    "",
    `Air: ${formatProjectedTemp(weather.controlWeather, units) || formatScheduleTempRange(weather.officialForecast, units) || "--"}`,
    trackTemp ? `Track Temperature: ${trackTemp}` : "",
    `Clouds: ${formatProjectedCloud(weather.controlWeather, weather.officialForecast) || "--"}`,
    `Humidity: ${formatProjectedHumidity(weather.controlWeather, weather.officialForecast) || "--"}`,
    `Wind: ${formatProjectedWind(weather.controlWeather, units) || "--"}`,
    `Precipitation: ${formatPrecipChance(weather.officialForecast) || "None"}`,
    `Time: ${weather.sessionTimeLabel || "--"}`,
  ];

  return lines.filter(Boolean).join("\n");
}

export function formatDateRange(
  start: string | null | undefined,
  end: string | null | undefined,
): string {
  if (!start) return "";
  const startDate = new Date(start);
  const startLabel = startDate.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  if (!end) return startLabel;
  const endDate = new Date(end);
  const endLabel = endDate.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return `${startLabel} - ${endLabel}`;
}

function formatRange(
  low: number | null | undefined,
  high: number | null | undefined,
  suffix: string,
): string | null {
  if (low == null && high == null) return null;
  if (low == null) return `${Math.round(high!)}${suffix}`;
  if (high == null) return `${Math.round(low)}${suffix}`;
  if (Math.abs(low - high) < 0.2) return `${Math.round(high)}${suffix}`;
  return `${Math.round(low)}-${Math.round(high)}${suffix}`;
}

function resolveCloudCoverPct(control: ControlWeather): number | null {
  if (control.cloudCoverPct != null) return control.cloudCoverPct;
  const label = control.cloudLabel?.toLowerCase() || "";
  if (label.includes("mostly clear")) return 20;
  if (label.includes("partly")) return 35;
  if (label.includes("mostly")) return 70;
  if (label.includes("overcast")) return 100;
  if (label.includes("clear")) return 0;
  if (label.includes("cloud")) return 85;
  return null;
}

function parseSessionHour(label: string | null | undefined): number | null {
  if (!label) return null;
  const match = label.trim().match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)?$/i);
  if (!match) return null;
  let hour = Number(match[1]);
  const minutes = match[2] ? Number(match[2]) : 0;
  const meridiem = match[3]?.toUpperCase();
  if (meridiem === "PM" && hour < 12) hour += 12;
  if (meridiem === "AM" && hour === 12) hour = 0;
  if (hour < 0 || hour > 23 || minutes < 0 || minutes > 59) return null;
  return hour + minutes / 60;
}

function estimateSolarFactor(hour: number | null, dateHint: string | null | undefined) {
  const parsedDate = dateHint ? new Date(dateHint) : null;
  const month = parsedDate && !Number.isNaN(parsedDate.getTime()) ? parsedDate.getUTCMonth() : 5;
  const seasonal = Math.cos(((month - 5) / 6) * Math.PI);
  const daylightHours = 12 + 2.4 * seasonal;
  const solarNoon = 13;
  const sunrise = solarNoon - daylightHours / 2;
  const sunset = solarNoon + daylightHours / 2;
  if (hour == null) return { factor: 0.45, sunrise, sunset, solarNoon };
  if (hour < sunrise || hour > sunset + 0.55) return { factor: 0, sunrise, sunset, solarNoon };
  const clampedHour = Math.min(hour, sunset);
  const position = (clampedHour - sunrise) / (sunset - sunrise);
  return { factor: clamp(Math.sin(position * Math.PI), 0, 1), sunrise, sunset, solarNoon };
}

function estimateStoredHeatF(
  hour: number | null,
  solar: { sunset: number; solarNoon: number },
  middayDeltaF: number,
): number {
  if (hour == null || hour <= solar.solarNoon || hour > solar.sunset + 3) return 0;
  if (hour <= solar.sunset) {
    const afternoonProgress = clamp((hour - solar.solarNoon) / (solar.sunset - solar.solarNoon), 0, 1);
    return middayDeltaF * 0.22 * Math.sin((afternoonProgress * Math.PI) / 2);
  }
  const remaining = clamp((solar.sunset + 3 - hour) / 3, 0, 1);
  return middayDeltaF * 0.22 * Math.pow(remaining, 1.25);
}

function estimateNightCoolingF(
  solarFactor: number,
  cloudFraction: number,
  humidityPct: number,
  windMph: number,
): number {
  if (solarFactor > 0.05) return 0;
  const clearSky = 1 - cloudFraction;
  const dryAir = 1 - humidityPct / 100;
  return clearSky * (1.4 + 1.2 * dryAir) + Math.min(Math.max(windMph, 0), 20) * 0.06;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function cToF(value: number): number {
  return value * 1.8 + 32;
}

function fToC(value: number): number {
  return (value - 32) / 1.8;
}

function cToFDelta(value: number): number {
  return value * 1.8;
}
